// NPM
import React, { useState, useEffect } from "react";
import _ from "lodash";
import * as d3 from "d3";

// State
import { useSelector } from "react-redux";

// Services
import apiService from "services/apiService";
import renderService from "services/renderService";

// Components
import SeatShareGraphRow from "./SeatShareGraphRow";

// Constants
import constants from "./constants";
import { usePartyOrder } from "../../hooks/usePartyOrder";

function SeatShareGraph() {
  // Hooks
  useEffect(() => loadData(), []);

  // State
  const [seatShareData, setSeatShareData] = useState({});
  const campaignConfig = useSelector((state) => state.campaignConfig.value);
  const responsiveView = useSelector((state) => state.responsiveView.value);
  const region = useSelector((state) => state.region.value);
  const parties = useSelector((state) => state.parties.value);
  const { orderedParties } = usePartyOrder();
  const partyIds = parties.map((p) => p.id);

  const loadData = async () => {
    const response = await fetch(apiService.apiURL("seat_share"));
    const jsonData = await response.json();
    setSeatShareData(jsonData);
  };

  // Short-circuit
  if (
    !renderService.canRender([seatShareData, region, parties, orderedParties])
  ) {
    return null;
  }

  // SVG Variables
  let rowHeight;
  let graphAreaWidth;
  let majorityLabelSize;
  let seatLabelSize;
  let majorityLabelOffset;
  let topLabelRowHeight = 30;
  let labelFontWeight;

  switch (responsiveView) {
    case "teeny":
    case "mobile":
      rowHeight = 65;
      graphAreaWidth = 350;
      majorityLabelOffset = 8;
      majorityLabelSize = 12;
      seatLabelSize = 14;
      break;

    case "tablet":
      rowHeight = 70;
      graphAreaWidth = 800;
      majorityLabelOffset = 6;
      majorityLabelSize = 14;
      seatLabelSize = 14;
      break;

    case "desktop":
    default:
      rowHeight = 80;
      graphAreaWidth = 1200;
      majorityLabelOffset = 10;
      majorityLabelSize = 16;
      seatLabelSize = 14;
      labelFontWeight = "bold";
      break;
  }

  // Make sure we're only rendering data that can be matched to a party
  const filteredSeatShareData = _.chain(seatShareData)
    .filter((ssd) => _.includes(partyIds, ssd.party))
    .filter((ssd) => ssd.region === region)
    .filter(
      (ssd) =>
        !(ssd.upper === "NA" || ssd.median === "NA" || ssd.lower === "NA"),
    )
    .sortBy((ssd) => orderedParties.indexOf(ssd.party))
    .value();

  // ComponentConfig
  const { majorityLabel, seatLabel } = _.get(
    campaignConfig,
    "sections.SeatShare",
    {},
  );

  const totalSeatNum = _.sumBy(filteredSeatShareData, (ssd) =>
    parseInt(ssd.median),
  );

  // Majority is set at 50% + 1
  const majoritySeatNum =
    Math.ceil(totalSeatNum / 2) + (totalSeatNum % 2 ? 0 : 1);

  const imageWidth = rowHeight - constants.imagePadding * 2;
  const graphStartX =
    imageWidth + constants.imagePadding * 2 + constants.imageRightMargin;

  // Create the viewBox.
  // Each row will have 50px (party image container), 100px (row graph)
  // Calculate the height by determining how many visible rows there are
  const viewBox = {
    width: graphAreaWidth + graphStartX,
    height: filteredSeatShareData.length * rowHeight + topLabelRowHeight,
  };

  // Get the xDomain
  let useableGraphWidth = graphAreaWidth - 50;
  let domain;
  let range;

  if (majoritySeatNum) {
    domain = [0, majoritySeatNum / 2, majoritySeatNum, totalSeatNum];

    range = [
      0,
      useableGraphWidth * 0.3,
      useableGraphWidth * 0.8,
      useableGraphWidth,
    ];
  } else {
    domain = [0, totalSeatNum - 1, totalSeatNum];
    range = [0, useableGraphWidth * 0.9, useableGraphWidth];
  }

  const xDomain = d3
    .scaleLinear()
    .domain(domain) // 0-<end_num>
    .range(range); // pixels

  return (
    <div className="Signal__section-visualization SeatShareGraph">
      <svg
        width="100%"
        preserveAspectRatio="xMinYMin"
        viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
      >
        {!_.isBlank(majoritySeatNum) && (
          <g transform={`translate(${graphStartX}, 0)`}>
            {/* Add the majority seat label */}
            <text
              x={xDomain(majoritySeatNum)}
              y={majorityLabelOffset}
              textAnchor="middle"
              dominantBaseline="central"
              fontWeight={labelFontWeight}
              fontSize={majorityLabelSize}
            >
              <tspan>{majorityLabel}</tspan>
            </text>

            <text
              x={xDomain(majoritySeatNum)}
              y={majorityLabelOffset + 17}
              textAnchor="middle"
              dominantBaseline="central"
              fontWeight="300"
              fontSize={seatLabelSize}
            >
              <tspan>{majoritySeatNum}</tspan>
              <tspan dx="3">{seatLabel}</tspan>
            </text>

            {/* Add the majority seat line */}
            <line
              className="SeatShareGraph__majoritySeatLine"
              x1={xDomain(majoritySeatNum)}
              y1={35}
              x2={xDomain(majoritySeatNum)}
              y2={viewBox.height}
              stroke="#CCC"
              strokeWidth="1"
              strokeDasharray="4 1"
            />
          </g>
        )}
        {filteredSeatShareData.map((seatShareDatum, index) => {
          return (
            <SeatShareGraphRow
              key={`SeatShareGraphRow-${index}`}
              seatShareDatum={seatShareDatum}
              rowIndex={index}
              numRows={filteredSeatShareData.length}
              xDomain={xDomain}
              majoritySeatNum={majoritySeatNum}
              totalSeatNum={totalSeatNum}
              dimensions={{
                viewBox,
                rowHeight,
                topLabelRowHeight,
              }}
            />
          );
        })}

        <g transform={`translate(${graphStartX}, 0)`}>
          <line
            className="SeatShareGraph__totalSeatLine"
            x1={xDomain(totalSeatNum)}
            y1={35}
            x2={xDomain(totalSeatNum)}
            y2={viewBox.height}
            stroke="#CCC"
            strokeWidth="1"
          />

          <text
            x={xDomain(totalSeatNum)}
            y={22}
            textAnchor="middle"
            dominantBaseline="central"
            fontWeight="300"
            fontSize={seatLabelSize}
          >
            <tspan>{totalSeatNum}</tspan>
            <tspan dx="3">{seatLabel}</tspan>
          </text>
        </g>
      </svg>
    </div>
  );
}

export default SeatShareGraph;
